import { z } from 'zod';
import { AVAILABILITY, EMBEDDED_LIMITS } from '../../constants/index.js';
import { defaulted, localeSchema, mediaRefSchema, objectIdSchema, optionalSlugSchema, priceSchema, requiredTranslatable, seoMetaSchema, slugSchema, translatableString, } from '../common.js';
import { auditableSchema, editableSchema, legacySchema, orderableSchema, publishableSchema, searchableSchema, } from './base.js';
export const availabilitySchema = z.enum(AVAILABILITY);
/**
 * Komplektatsiya — bitta mahsulotning turli to'plamlari (TZ 3.9).
 * Masalan DJI Mavic 3 Pro: 4 ta komplektatsiya, 4 xil narx.
 *
 * `price: null` → «Narxni so'rang». Alohida boolean YO'Q (ADR-004).
 */
export const productVariantSchema = z.object({
    _id: z.string().min(1).max(40),
    title: requiredTranslatable({ max: 300 }),
    sku: z.string().trim().max(60).nullable().default(null),
    price: priceSchema,
    availability: availabilitySchema.default('on_order'),
    /** Komplekt tarkibi: «Dron, pult, 3 ta batareya, ...» */
    includes: translatableString({ max: 2000 }),
    image: mediaRefSchema.nullable().default(null),
    order: z.number().int().min(0).default(0),
    isVisible: z.boolean().default(true),
});
/**
 * Xarakteristikalar guruhi — Google Drive'dagi PDF larni almashtiradi (TZ 7.3).
 * Masalan: «Kamera» guruhi ichida «Asosiy sensor → Hasselblad 4/3 CMOS, 20 MP».
 */
export const specGroupSchema = z.object({
    title: requiredTranslatable({ max: 200 }),
    rows: z
        .array(z.object({
        key: requiredTranslatable({ max: 200 }),
        value: translatableString({ max: 1000 }),
    }))
        .max(EMBEDDED_LIMITS.SPEC_ROWS_PER_GROUP)
        .default([]),
    order: z.number().int().min(0).default(0),
});
export const productDocumentSchema = z.object({
    title: requiredTranslatable({ max: 300 }),
    file: mediaRefSchema,
    order: z.number().int().min(0).default(0),
});
export const productSchema = auditableSchema
    .merge(legacySchema)
    .merge(publishableSchema)
    .merge(orderableSchema)
    .merge(editableSchema)
    .merge(searchableSchema)
    .extend({
    slug: slugSchema,
    title: requiredTranslatable({ max: 300 }),
    shortDescription: translatableString({ max: 1000 }),
    /** Sanitize qilingan HTML (TipTap) */
    description: translatableString(),
    brandId: objectIdSchema.nullable().default(null),
    categoryIds: z.array(objectIdSchema).max(10).default([]),
    coverImage: mediaRefSchema.nullable().default(null),
    gallery: z.array(mediaRefSchema).max(EMBEDDED_LIMITS.GALLERY_IMAGES).default([]),
    video: z
        .object({
        source: z.enum(['youtube', 'upload']),
        url: z.string().max(300),
        poster: mediaRefSchema.nullable().default(null),
    })
        .nullable()
        .default(null),
    specGroups: z.array(specGroupSchema).max(EMBEDDED_LIMITS.SPEC_GROUPS).default([]),
    documents: z.array(productDocumentSchema).max(20).default([]),
    variants: z.array(productVariantSchema).max(EMBEDDED_LIMITS.PRODUCT_VARIANTS).default([]),
    // ── Denormalizatsiya (ISH-REJA §4.4) ────────────────────────────────────
    // Katalog filtri va saralashi shu maydonlar bo'yicha ishlaydi — aks holda
    // har bir so'rovda `$unwind` kerak bo'lardi va indeks ishlatilmasdi.
    // Faqat ProductsRepository.save() da yangilanadi.
    priceMin: z.number().int().nullable().default(null),
    priceMax: z.number().int().nullable().default(null),
    /**
     * Kamida bitta komplektatsiyaning narxi bormi.
     *
     * MongoDB `null` ni har qanday sondan KICHIK deb saralaydi, shuning uchun
     * «arzondan qimmatga» tartibida «Narxni so'rang» pozitsiyalari birinchi
     * chiqib qolardi. Shu bayroq bo'yicha saralab, ular oxiriga suriladi.
     */
    hasPrice: z.boolean().default(false),
    availabilityAgg: availabilitySchema.default('out_of_stock'),
    variantCount: z.number().int().min(0).default(0),
    /** Qidiruv uchun normalizatsiya qilingan matn (til bo'yicha) */
    searchText: defaulted(z.object({
        uz: z.string().default(''),
        ru: z.string().default(''),
        en: z.string().default(''),
    })),
    isFeatured: z.boolean().default(false),
    seo: defaulted(seoMetaSchema),
});
export const productCategorySchema = auditableSchema
    .merge(orderableSchema)
    .merge(publishableSchema)
    .merge(editableSchema)
    .extend({
    slug: slugSchema,
    name: requiredTranslatable({ max: 200 }),
    description: translatableString({ max: 2000 }),
    icon: z.string().trim().max(40).nullable().default(null),
    image: mediaRefSchema.nullable().default(null),
    /** Maksimum 2 daraja (TZ 3.10) */
    parentId: objectIdSchema.nullable().default(null),
    /** Denormalizatsiya: mahsulot saqlanganda yangilanadi */
    productCount: z.number().int().min(0).default(0),
    seo: defaulted(seoMetaSchema),
});
export const brandSchema = auditableSchema
    .merge(orderableSchema)
    .merge(publishableSchema)
    .merge(editableSchema)
    .extend({
    slug: slugSchema,
    name: z.string().trim().min(1).max(120),
    logo: mediaRefSchema.nullable().default(null),
    description: translatableString({ max: 2000 }),
});
// ─── Kirish sxemalari (admin formalari) ──────────────────────────────────────
export const createProductSchema = productSchema
    .omit({
    _id: true,
    createdAt: true,
    updatedAt: true,
    createdBy: true,
    updatedBy: true,
    deletedAt: true,
    priceMin: true,
    priceMax: true,
    hasPrice: true,
    availabilityAgg: true,
    variantCount: true,
    searchText: true,
    publishedAt: true,
    isPublished: true,
})
    .extend({ slug: optionalSlugSchema });
export const updateProductSchema = createProductSchema.partial();
/** Katalog filtri (TZ 6.1). */
/**
 * Manzildagi ro'yxat: `?category=vtol,fpv` yoki `?category=vtol&category=fpv`.
 *
 * Maketda filtr KATAKCHALAR bilan — mijoz bir vaqtda bir nechta
 * kategoriyani tanlay oladi (`C2 Katalog.dc.html`). Ikkala yozuvi ham
 * qabul qilinadi: vergulli variant havolani qisqartiradi, takroriy
 * parametr esa oddiy `<form method="get">` yuborganda o'zi hosil
 * bo'ladi.
 *
 * Bo'sh qiymatlar tashlab yuboriladi — `?category=` yozuvi butun
 * filtrni yiqitmasligi kerak.
 */
function queryList(item, max) {
    return z.preprocess((value) => {
        if (value === undefined || value === null || value === '')
            return undefined;
        const raw = Array.isArray(value) ? value : [value];
        const flat = raw.flatMap((entry) => (typeof entry === 'string' ? entry.split(',') : [entry]));
        const cleaned = flat
            .map((entry) => (typeof entry === 'string' ? entry.trim() : entry))
            .filter((entry) => entry !== '' && entry !== undefined && entry !== null);
        return cleaned.length > 0 ? cleaned : undefined;
    }, z.array(item).max(max).optional());
}
export const listProductsQuerySchema = z.object({
    page: z.coerce.number().int().min(1).max(100).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    category: queryList(slugSchema, 20),
    brand: queryList(slugSchema, 20),
    priceMin: z.coerce.number().int().nonnegative().optional(),
    priceMax: z.coerce.number().int().nonnegative().optional(),
    availability: queryList(availabilitySchema, 3),
    sort: z.enum(['popular', 'price-asc', 'price-desc', 'newest']).default('popular'),
    search: z.string().trim().max(200).optional(),
    // `localeSchema` — tillar ro'yxati BITTA joyda (`LOCALES`). Qo'lda
    // sanalganda `oz` qo'shilganda katalog uni 422 bilan rad etardi.
    lang: localeSchema.optional(),
});
//# sourceMappingURL=catalog.js.map